import {apiService, getAuthValues, setAuthHeader} from "./api";
import {
    User,
    Request,
    RequestAPIResponse,
    Verification,
    Participant
} from "../models";

type RequestCreationProps = {
    description: string
    recipients: string[]
    resolver: string
    title: string
};
// What an action answers: it runs at once, or once the user's phone approved it
// (when their company requires approval; helper/approvals waits for it).
type ActionDone = {message?: string, request?: any};
type Decision = {id: string, note?: string, proceed: boolean};
export type Maybe<T> =
| {type: "success", value: T}
| {type: "error", message: string, code?: string};

function deduplicate(val: User[]): User[] {
    const map = val.reduce(function (acc, user) {
        if (!acc[user.email]) {
            acc[user.email] = user;
        }
        return acc;
    }, Object.create(null));
    return Object.values(map);
}

async function searchUsers(query: string): Promise<User[]> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    res = await apiService.get<{results: User[]}>(
        `/company?type=searchUser&searchQuery=${query}`
    );
    return deduplicate(res.data.results);
}

async function fetchUsers(): Promise<User[]> {
    const {companyId, token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    res = await apiService.get<User[]>(
        `/company?type=getCompanyUsers&companyId=${companyId}`
    );
    return res.data;
}

async function fetchParticipants(id: string): Promise<Maybe<Participant[]>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.get<{participants: Participant[]}>(
            `/request/participants?id=${id}`
        );
        return {type: "success", value: res.data.participants};
    } catch (e: any) {
        res = e.response?.data?.message ?? "Failed to fetch participants";
        return {message: res, type: "error"};
    }
}

async function fetchRequests(): Promise<Maybe<Request[]>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.get<{requests: RequestAPIResponse[]}>(
            "/request/list"
        );
        return {
            type: "success",
            value: res.data.requests.map(RequestAPIResponse.toRequest)
        };
    } catch (err: any) {
        res = err.response?.data?.message ?? "Failed to retrieve requests";
        return {message: res, type: "error"};
    }
}

async function create(data: RequestCreationProps): Promise<Maybe<ActionDone>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.post<ActionDone>(
            "/request/",
            data
        );
        return {type: "success", value: res.data};
    } catch (err: any) {
        res = err.response?.data?.message;
        return {
            code: err.response?.data?.code,
            message: res ?? "Failed to create the request",
            type: "error"
        };
    }
}

async function cancel(id: string): Promise<Maybe<ActionDone>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.post<ActionDone>(
            "/request/cancel",
            {id}
        );
        return {type: "success", value: res.data};
    } catch (err: any) {
        res = err.response?.data?.message;
        return {
            code: err.response?.data?.code,
            message: res ?? "Failed to cancel the request",
            type: "error"
        };
    }
}

async function assignResolver(id: string, email: string): Promise<Maybe<ActionDone>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.post<ActionDone>(
            "/request/reassign",
            {email, id}
        );
        return {type: "success", value: res.data};
    } catch (err: any) {
        res = err.response?.data?.message;
        return {
            code: err.response?.data?.code,
            message: res ?? "Failed to assign the resolver",
            type: "error"
        };
    }
}

async function resolve(p: Decision): Promise<Maybe<ActionDone>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.post<ActionDone>(
            "/request/resolve",
            p
        );
        return {type: "success", value: res.data};
    } catch (err: any) {
        res = err.response?.data?.message;
        return {
            code: err.response?.data?.code,
            message: res ?? "Failed to record the decision",
            type: "error"
        };
    }
}

/**
 * Withdraw an intent not yet confirmed in Admiral Access (the same call as
 * declining it there). One that already expired is gone either way.
 */
async function revokeIntent(token: string): Promise<Maybe<string>> {
    const {token: auth} = getAuthValues();
    let res;
    setAuthHeader(auth!);
    try {
        await apiService.post("/request/verify", {approve: false, token});
        return {type: "success", value: token};
    } catch (err: any) {
        if ([404, 410].includes(err.response?.status)) {
            return {type: "success", value: token};
        }
        res = err.response?.data?.message;
        return {message: res ?? "Failed to cancel the pending request", type: "error"};
    }
}

async function getIntents(): Promise<Maybe<Verification[]>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.get<{results: Verification[]}>("/request/intents");
        return  {type: "success", value: res.data.results};
    } catch (err: any) {
        res = err.response.data.message;
        return {message: res ?? "Failed to fetch pending intents", type: "error"}
    }
}

export {
    assignResolver,
    cancel,
    create,
    fetchParticipants,
    fetchRequests,
    fetchUsers,
    getIntents,
    resolve,
    revokeIntent,
    searchUsers
};
